
import React, { useState } from 'react';

const LandingPage = () => {
    const [lang, setLang] = useState('en');

    const translations = {
        en: {
            services: "Services",
            contact: "Contact",
            getStarted: "Get Started",
            heroTitle: "Digital Excellence",
            heroTitleSpan: "Redefined",
            heroDesc: "We craft premium web experiences and software solutions that elevate your brand. Specializing in cutting-edge design and robust implementation.",
            startProject: "Start a Project",
            exploreServices: "Explore Services",
            ourServices: "Our Services",
            ourServicesDesc: "Comprehensive digital solutions tailored to your business needs.",
            webDesignTitle: "Web Design & Dev",
            webDesignDesc: "Stunning, responsive websites built with modern frameworks like React, Next.js, and Tailwind CSS.",
            softwareTitle: "Software Solutions",
            softwareDesc: "Custom software development, internal tools, and mobile applications designed for scalability.",
            brandTitle: "Brand Identity",
            brandDesc: "Complete branding packages including logo design, color theory, and digital asset creation.",
            readyTitle: "Ready to elevate your digital presence?",
            readyDesc: "Let's build something extraordinary together.",
            getInTouch: "Get in Touch",
            getInTouchDesc: "Whether you have a project in mind or just want to chat about technology, we'd love to hear from you.",
            email: "Email",
            phone: "Phone",
            privacyPolicy: "Privacy Policy",
            copyright: "Copyright © 2024 - All right reserved by escuDesign Ltd",
            footerServices: "Services",
            footerContact: "Contact",
        },
        ro: {
            services: "Servicii",
            contact: "Contact",
            getStarted: "Începe Acum",
            heroTitle: "Excelență Digitală",
            heroTitleSpan: "Redefinită",
            heroDesc: "Creăm experiențe web premium și soluții software care îți elevează brandul. Specializați în design de ultimă oră și implementări robuste.",
            startProject: "Începe un Proiect",
            exploreServices: "Explorează Serviciile",
            ourServices: "Serviciile Noastre",
            ourServicesDesc: "Soluții digitale complete adaptate nevoilor afacerii tale.",
            webDesignTitle: "Web Design & Dev",
            webDesignDesc: "Site-uri uimitoare, receptive, construite cu framework-uri moderne precum React, Next.js și Tailwind CSS.",
            softwareTitle: "Soluții Software",
            softwareDesc: "Dezvoltare software personalizată, instrumente interne și aplicații mobile proiectate pentru scalabilitate.",
            brandTitle: "Identitate de Brand",
            brandDesc: "Pachete complete de branding incluzând design de logo, teoria culorilor și crearea de active digitale.",
            readyTitle: "Gata să îți elevezi prezența digitală?",
            readyDesc: "Hai să construim ceva extraordinar împreună.",
            getInTouch: "Contactează-ne",
            getInTouchDesc: "Fie că ai un proiect în minte sau vrei doar să discutăm despre tehnologie, ne-ar plăcea să te auzim.",
            email: "Email",
            phone: "Telefon",
            privacyPolicy: "Politica de Confidențialitate",
            copyright: "Drepturi de autor © 2024 - Toate drepturile rezervate de escuDesign Ltd",
            footerServices: "Servicii",
            footerContact: "Contact",
        }
    };

    const t = translations[lang];

    return (
        <div className="min-h-screen bg-base-200 font-sans text-base-content">
            {/* Navbar */}
            <div className="navbar bg-base-100/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
                <div className="navbar-start">
                    <div className="dropdown">
                        <div tabIndex={0} role="button" className="btn btn-ghost lg:hidden">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16" /></svg>
                        </div>
                        <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52">
                            <li><a href="#services">{t.services}</a></li>
                            <li><a href="#contact">{t.contact}</a></li>
                            <li className="mt-2 pt-2 border-t border-base-content/10">
                                <div className="flex gap-2 justify-center">
                                    <button className={`btn btn-sm btn-ghost ${lang === 'ro' ? 'btn-active' : ''}`} onClick={() => setLang('ro')}>🇷🇴</button>
                                    <button className={`btn btn-sm btn-ghost ${lang === 'en' ? 'btn-active' : ''}`} onClick={() => setLang('en')}>🇬🇧</button>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <a href="/" className="btn btn-ghost text-xl font-bold tracking-tighter">escuDesign<span className="text-secondary">.</span></a>
                </div>
                <div className="navbar-center hidden lg:flex">
                    <ul className="menu menu-horizontal px-1 font-medium">
                        <li><a href="#services">{t.services}</a></li>
                        <li><a href="#contact">{t.contact}</a></li>
                    </ul>
                </div>
                <div className="navbar-end gap-2">
                    <button className={`hidden lg:inline-flex btn btn-sm btn-ghost ${lang === 'ro' ? 'btn-active' : ''}`} onClick={() => setLang('ro')}>🇷🇴</button>
                    <button className={`hidden lg:inline-flex btn btn-sm btn-ghost ${lang === 'en' ? 'btn-active' : ''}`} onClick={() => setLang('en')}>🇬🇧</button>
                    <a href="#contact" className="btn btn-secondary btn-sm ml-2">{t.getStarted}</a>
                </div>
            </div>

            {/* Hero Section */}
            <div className="hero min-h-[90vh]" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1550745165-9bc0b252726f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)' }}>
                <div className="hero-overlay bg-opacity-70 bg-black"></div>
                <div className="hero-content text-center text-neutral-content p-4">
                    <div className="max-w-4xl px-2">
                        <h1 className="mb-6 text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight text-white leading-tight">
                            {t.heroTitle} <br className="hidden sm:block" /> <span className="text-secondary">{t.heroTitleSpan}</span>
                        </h1>
                        <p className="mb-10 text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
                            {t.heroDesc}
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4 px-4">
                            <a href="#contact" className="btn btn-primary btn-md sm:btn-lg w-full sm:w-auto">{t.startProject}</a>
                            <a href="#services" className="btn btn-outline text-white hover:bg-white hover:text-black btn-md sm:btn-lg w-full sm:w-auto">{t.exploreServices}</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Services Section */}
            <section id="services" className="py-20 bg-base-100">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">{t.ourServices}</h2>
                        <p className="text-lg opacity-70 max-w-2xl mx-auto">{t.ourServicesDesc}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Service Card 1 */}
                        <div className="card bg-base-200 shadow-xl hover:-translate-y-2 transition-transform duration-300 border border-base-content/5">
                            <div className="card-body items-center text-center">
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 text-primary">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                                    </svg>
                                </div>
                                <h3 className="card-title mb-2">{t.webDesignTitle}</h3>
                                <p className="opacity-70">{t.webDesignDesc}</p>
                            </div>
                        </div>

                        {/* Service Card 2 */}
                        <div className="card bg-base-200 shadow-xl hover:-translate-y-2 transition-transform duration-300 border border-base-content/5">
                            <div className="card-body items-center text-center">
                                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-4 text-secondary">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                                    </svg>
                                </div>
                                <h3 className="card-title mb-2">{t.softwareTitle}</h3>
                                <p className="opacity-70">{t.softwareDesc}</p>
                            </div>
                        </div>

                        {/* Service Card 3 */}
                        <div className="card bg-base-200 shadow-xl hover:-translate-y-2 transition-transform duration-300 border border-base-content/5">
                            <div className="card-body items-center text-center">
                                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-4 text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                    </svg>
                                </div>
                                <h3 className="card-title mb-2">{t.brandTitle}</h3>
                                <p className="opacity-70">{t.brandDesc}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio / Featured Work - HIDDEN
            <section id="portfolio" className="py-20 bg-base-200">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                        <div>
                            <h2 className="text-4xl font-bold mb-4">Featured Projects</h2>
                            <p className="opacity-70">A selection of our recent work.</p>
                        </div>
                        <a href="#" className="btn btn-link text-secondary no-underline hover:underline mt-4 md:mt-0 px-0">View All Projects →</a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="card bg-base-100 shadow-xl overflow-hidden group">
                            <figure className="h-48 bg-neutral relative overflow-hidden">
                                <img src="https://images.unsplash.com/photo-1481487484168-9b930d55208d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Project 1" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <button className="btn btn-outline btn-sm text-white">View Case Study</button>
                                </div>
                            </figure>
                            <div className="card-body p-6">
                                <div className="badge badge-primary badge-outline mb-2">Legal Tech</div>
                                <h3 className="card-title text-lg">LawFirm Portal</h3>
                                <p className="text-sm opacity-70">Secure client portal and document management system.</p>
                            </div>
                        </div>

                        <div className="card bg-base-100 shadow-xl overflow-hidden group">
                            <figure className="h-48 bg-neutral relative overflow-hidden">
                                <img src="https://images.unsplash.com/photo-1555421689-d68471e189f2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Project 2" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <button className="btn btn-outline btn-sm text-white">View Case Study</button>
                                </div>
                            </figure>
                            <div className="card-body p-6">
                                <div className="badge badge-secondary badge-outline mb-2">E-Commerce</div>
                                <h3 className="card-title text-lg">Fashion Boutique</h3>
                                <p className="text-sm opacity-70">High-conversion Shopify storefront with custom theme.</p>
                            </div>
                        </div>

                        <div className="card bg-base-100 shadow-xl overflow-hidden group">
                            <figure className="h-48 bg-neutral relative overflow-hidden">
                                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Project 3" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <button className="btn btn-outline btn-sm text-white">View Case Study</button>
                                </div>
                            </figure>
                            <div className="card-body p-6">
                                <div className="badge badge-accent badge-outline mb-2">SaaS</div>
                                <h3 className="card-title text-lg">Analytics Dashboard</h3>
                                <p className="text-sm opacity-70">Real-time data visualization platform for marketing teams.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            */}

            {/* CTA Section */}
            <section className="py-20 bg-primary text-primary-content">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to elevate your digital presence?</h2>
                    <p className="text-xl mb-8 opacity-90">Let's build something extraordinary together.</p>
                    {/* <a href="#contact" className="btn btn-lg btn-white text-primary hover:bg-base-100 hover:text-primary transition-colors border-0">Start a Project</a> */}
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 bg-base-100">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col items-center text-center">
                        <h2 className="text-4xl font-bold mb-6">Get in Touch</h2>
                        <p className="opacity-70 mb-12 max-w-md">Whether you have a project in mind or just want to chat about technology, we'd love to hear from you.</p>

                        <div className="flex flex-col md:flex-row gap-8 md:gap-16">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center text-secondary shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-xl mb-1">Email</h3>
                                    <a href="mailto:popescu.developer@gmail.com" className="link link-hover text-lg">popescu.developer@gmail.com</a>
                                </div>
                            </div>

                            <div className="flex flex-col items-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center text-secondary shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-xl mb-1">Phone</h3>
                                    <a href="tel:+40755801083" className="link link-hover text-lg">+40 755 801 083</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            {/* Footer */}
            <footer className="footer footer-center p-10 bg-neutral text-neutral-content rounded">
                <nav className="grid grid-flow-col gap-4">
                    <a href="#services" className="link link-hover">{t.footerServices}</a>
                    <a href="#contact" className="link link-hover">{t.footerContact}</a>
                    <a href="/" className="link link-hover">{t.privacyPolicy}</a>
                </nav>
                <aside>
                    <p>{t.copyright}</p>
                </aside>
            </footer>
        </div>
    );
};

export default LandingPage;
